import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faUser, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { openWindow } from "../../Slices/windowsSlice";
import { openNotesApp } from "../webtopUiSlice";
import { useFilteredSideMenu, flattenMenu, getMenuIcon, WEBTOP_SHELL_APPS } from "../sideMenu";
import { searchApps } from "./search";
import { useAppLogout } from "../useAppLogout";

/**
 * StartMenu — Windows 11-style search/launcher opened by the taskbar Start
 * button (or Ctrl/Cmd+K). Searches every app + submenu the user can access
 * (flattened SIDEMENU, access-filtered), and launches the chosen one in a
 * window. Keyboard: ↑/↓ to move, Enter to launch, Esc to close.
 */
const StartMenu = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const logout = useAppLogout();
  const me = useSelector((s) => s?.loginUserInfo?.loginUserDetails) || {};
  const fullName = [me.firstname, me.lastname].filter(Boolean).join(" ").trim() || "Account";
  const initials =
    ((me.firstname || me.lastname || "?").trim()[0] || "?").toUpperCase() +
    ((me.lastname || "").trim()[0] || "").toUpperCase();
  const [acctOpen, setAcctOpen] = useState(false);
  // Strictly access-filtered: a user only sees apps their RouteAccess allows.
  const menu = useFilteredSideMenu();
  // Built-in shell apps (Sticky Notes, …) are searchable alongside the access
  // apps and shown for everyone (they aren't backend routes).
  const apps = useMemo(() => {
    const shell = WEBTOP_SHELL_APPS.map((a) => ({
      key: `shell:${a.key}`,
      title: a.name,
      section: "Webtop",
      shell: a,
    }));
    return [...shell, ...flattenMenu(menu)];
  }, [menu]);

  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const results = useMemo(() => searchApps(apps, query), [apps, query]);

  // Reset + focus when opened.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSel(0);
    setAcctOpen(false);
    const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  // Keep selection in range and scrolled into view.
  useEffect(() => {
    setSel((s) => Math.min(s, Math.max(0, results.length - 1)));
  }, [results.length]);
  useEffect(() => {
    const node = listRef.current && listRef.current.querySelector(".webtop-startitem--sel");
    if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ block: "nearest" });
  }, [sel]);

  if (!open) return null;

  const launch = (app) => {
    if (!app) return;
    if (app.shell) {
      if (app.shell.action === "openNotesApp") dispatch(openNotesApp());
      else if (app.shell.openPath)
        dispatch(openWindow({ path: app.shell.openPath, title: app.shell.name }));
    } else {
      dispatch(openWindow({ path: app.path, title: app.title }));
    }
    onClose();
  };

  const openProfile = () => {
    dispatch(openWindow({ path: "/profile", title: "Profile", deduplicate: true }));
    onClose();
  };
  const signOut = () => {
    onClose();
    logout();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      launch(results[sel]);
    }
  };

  // Render to <body> via a portal so the full-screen backdrop actually covers
  // the viewport. (Rendered inside the taskbar, the taskbar's backdrop-filter
  // traps fixed children to the taskbar's box, so the backdrop never reached the
  // desktop and outside-clicks didn't close the menu.)
  const node = (
    <>
      <div className="webtop-start-backdrop" onMouseDown={onClose} />
      <div className="webtop-startmenu" role="dialog" aria-label="Start" onKeyDown={onKeyDown}>
        <div className="webtop-startmenu__search">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="webtop-startmenu__searchicon" />
          <input
            ref={inputRef}
            className="webtop-startmenu__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search apps"
            aria-label="Search apps"
          />
        </div>

        <div className="webtop-startmenu__label">{query.trim() ? "Results" : "All apps"}</div>

        <div className="webtop-startmenu__results" ref={listRef}>
          {results.length === 0 && <div className="webtop-startmenu__empty">No apps found</div>}
          {results.map((app, i) => (
            <button
              key={app.key}
              type="button"
              className={`webtop-startitem${i === sel ? " webtop-startitem--sel" : ""}`}
              onClick={() => launch(app)}
              onMouseEnter={() => setSel(i)}
            >
              <span className="webtop-startitem__glyph">
                <FontAwesomeIcon icon={app.shell ? app.shell.icon : getMenuIcon(app.section)} />
              </span>
              <span className="webtop-startitem__text">
                <span className="webtop-startitem__title">{app.title}</span>
                <span className="webtop-startitem__section">{app.section}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Windows-style account row (avatar + name) → Profile / Sign out */}
        <div className="webtop-startmenu__account">
          {acctOpen && (
            <div className="webtop-startmenu__acctmenu" role="menu">
              <button type="button" className="webtop-startmenu__acctitem" onClick={openProfile}>
                <FontAwesomeIcon icon={faUser} /> Profile
              </button>
              <button
                type="button"
                className="webtop-startmenu__acctitem webtop-startmenu__acctitem--danger"
                onClick={signOut}
              >
                <FontAwesomeIcon icon={faRightFromBracket} /> Sign out
              </button>
            </div>
          )}
          <button
            type="button"
            className="webtop-startmenu__user"
            onClick={() => setAcctOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={acctOpen}
            title={fullName}
          >
            <span className="webtop-startmenu__avatar">{initials}</span>
            <span className="webtop-startmenu__username">{fullName}</span>
            <FontAwesomeIcon icon={faRightFromBracket} className="webtop-startmenu__userlogout" />
          </button>
        </div>
      </div>
    </>
  );

  if (typeof document === "undefined" || !document.body) return node;
  return ReactDOM.createPortal(node, document.body);
};

export default StartMenu;
